import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Save, Trash2, X, Edit3, Tag, Car, Moon, Package } from "lucide-react";
import api from "@/lib/api";
import AddOnsManager from "@/components/admin/AddOnsManager";

/**
 * Admin Pricing Dashboard — single source of truth for:
 *   • Popular routes (base price, optional night price, active/inactive)
 *   • Vehicle categories (multiplier, capacity, active)
 *   • Global settings (night multiplier fallback)
 *
 * All mutations refresh the backend runtime cache, so changes apply to the
 * very next customer quote — no restart needed.
 */
export default function AdminPricing() {
  const [tab, setTab] = useState("routes");
  const [routes, setRoutes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({ night_multiplier: 1.2 });
  const [filterCategory, setFilterCategory] = useState("standard");
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [editingCatId, setEditingCatId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const loadAll = async () => {
    try {
      const [r, c, s] = await Promise.all([
        api.get("/admin/pricing/routes"),
        api.get("/admin/pricing/vehicle-categories"),
        api.get("/admin/pricing/settings"),
      ]);
      setRoutes(r.data || []);
      setCategories(c.data || []);
      setSettings(s.data || { night_multiplier: 1.2 });
    } catch (e) {
      toast.error("Failed to load pricing data");
    }
  };
  useEffect(() => { loadAll(); }, []);

  const selectedMult = useMemo(() => {
    const c = categories.find((c) => c.key === filterCategory);
    return c ? c.multiplier : 1.0;
  }, [categories, filterCategory]);

  const previewPrice = (base) => Number(base * selectedMult).toFixed(2);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur bg-zinc-950/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin/dashboard" data-testid="pricing-back"
                className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#C5A059]">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="text-right">
            <h1 className="font-display text-2xl">Pricing</h1>
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Source of truth for every quote</div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <TabButton active={tab === "routes"} onClick={() => setTab("routes")} icon={Tag} testid="tab-routes">
            Routes ({routes.length})
          </TabButton>
          <TabButton active={tab === "categories"} onClick={() => setTab("categories")} icon={Car} testid="tab-categories">
            Vehicle Categories ({categories.length})
          </TabButton>
          <TabButton active={tab === "addons"} onClick={() => setTab("addons")} icon={Package} testid="tab-addons">
            Add-Ons / Extras
          </TabButton>
          <TabButton active={tab === "settings"} onClick={() => setTab("settings")} icon={Moon} testid="tab-settings">
            Settings
          </TabButton>
        </div>

        {/* Add-Ons / Extras */}
        {tab === "addons" && <AddOnsManager />}

        {/* Routes */}
        {tab === "routes" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Preview as:</span>
                {categories.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`filter-${c.key}`}
                    onClick={() => setFilterCategory(c.key)}
                    className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors ${
                      filterCategory === c.key
                        ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
                        : "border-white/10 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {c.label} · ×{c.multiplier}
                  </button>
                ))}
              </div>
              <button
                data-testid="add-route-btn"
                onClick={() => setShowNew(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C5A059] text-black text-sm font-medium hover:bg-[#D4AF37]"
              >
                <Plus className="w-4 h-4" /> Add route
              </button>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-white/5 bg-zinc-900/50">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/5">
                  <tr>
                    <th className="text-left px-5 py-4">Route</th>
                    <th className="text-right px-3 py-4">Base €</th>
                    <th className="text-right px-3 py-4">Night €</th>
                    <th className="text-right px-3 py-4">Preview ({filterCategory})</th>
                    <th className="text-right px-3 py-4">Status</th>
                    <th className="text-right px-5 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map((r) => (
                    <RouteRow
                      key={r.id}
                      route={r}
                      isEditing={editingRouteId === r.id}
                      previewPrice={previewPrice(r.base_price_eur)}
                      onEdit={() => setEditingRouteId(r.id)}
                      onCancel={() => setEditingRouteId(null)}
                      onSaved={async () => { setEditingRouteId(null); await loadAll(); }}
                    />
                  ))}
                  {routes.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-12 text-zinc-500">No routes yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {showNew && <NewRouteModal onClose={() => setShowNew(false)} onSaved={async () => { setShowNew(false); await loadAll(); }} />}
          </div>
        )}

        {/* Categories */}
        {tab === "categories" && (
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/5">
                <tr>
                  <th className="text-left px-5 py-4">Category</th>
                  <th className="text-right px-3 py-4">Capacity</th>
                  <th className="text-right px-3 py-4">Multiplier</th>
                  <th className="text-right px-3 py-4">€180 route becomes</th>
                  <th className="text-right px-3 py-4">Status</th>
                  <th className="text-right px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <CategoryRow
                    key={c.id}
                    cat={c}
                    isEditing={editingCatId === c.id}
                    onEdit={() => setEditingCatId(c.id)}
                    onCancel={() => setEditingCatId(null)}
                    onSaved={async () => { setEditingCatId(null); await loadAll(); }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Settings */}
        {tab === "settings" && (
          <SettingsPanel settings={settings} onSaved={loadAll} />
        )}

        <div className="mt-10 text-[11px] uppercase tracking-[0.25em] text-zinc-600">
          Every change applies to the next quote request instantly · no restart required
        </div>
      </div>
    </div>
  );
}

/* --------------------------- sub-components --------------------------- */

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm border transition-colors ${
        active
          ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
          : "border-white/10 text-zinc-400 hover:text-white"
      }`}
    >
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

function RouteRow({ route, isEditing, previewPrice, onEdit, onCancel, onSaved }) {
  const [base, setBase] = useState(route.base_price_eur);
  const [night, setNight] = useState(route.night_price_eur ?? "");
  const [active, setActive] = useState(route.active !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBase(route.base_price_eur);
    setNight(route.night_price_eur ?? "");
    setActive(route.active !== false);
  }, [route]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/pricing/routes/${route.id}`, {
        base_price_eur: Number(base),
        night_price_eur: night === "" ? null : Number(night),
        active,
      });
      toast.success("Route updated · applies to the next quote");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete route ${route.pickup} ↔ ${route.dropoff}?`)) return;
    try {
      await api.delete(`/admin/pricing/routes/${route.id}`);
      toast.success("Route removed");
      onSaved();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <tr data-testid={`route-row-${route.key}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
      <td className="px-5 py-4">
        <div className="text-white">{route.pickup} <span className="text-zinc-500 mx-1">↔</span> {route.dropoff}</div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{route.key}</div>
      </td>
      <td className="px-3 py-4 text-right">
        {isEditing ? (
          <input type="number" step="0.01" value={base} onChange={(e) => setBase(e.target.value)}
                 data-testid={`route-base-${route.key}`}
                 className="w-24 bg-zinc-800 border border-white/10 rounded px-2 py-1 text-right" />
        ) : `€${route.base_price_eur}`}
      </td>
      <td className="px-3 py-4 text-right text-zinc-400">
        {isEditing ? (
          <input type="number" step="0.01" value={night} placeholder="auto" onChange={(e) => setNight(e.target.value)}
                 className="w-24 bg-zinc-800 border border-white/10 rounded px-2 py-1 text-right" />
        ) : (route.night_price_eur ? `€${route.night_price_eur}` : <span className="text-zinc-600">auto</span>)}
      </td>
      <td className="px-3 py-4 text-right text-[#C5A059]">€{previewPrice}</td>
      <td className="px-3 py-4 text-right">
        {isEditing ? (
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>{active ? "Active" : "Off"}</span>
          </label>
        ) : (
          <span className={`text-xs uppercase tracking-wider ${route.active !== false ? "text-emerald-400" : "text-zinc-600"}`}>
            {route.active !== false ? "Active" : "Off"}
          </span>
        )}
      </td>
      <td className="px-5 py-4 text-right">
        {isEditing ? (
          <div className="inline-flex gap-2">
            <button onClick={save} disabled={saving} data-testid={`save-route-${route.key}`}
                    className="p-2 rounded-full bg-[#C5A059] text-black hover:bg-[#D4AF37] disabled:opacity-50">
              <Save className="w-4 h-4" />
            </button>
            <button onClick={onCancel} className="p-2 rounded-full border border-white/10 text-zinc-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="inline-flex gap-1">
            <button onClick={onEdit} data-testid={`edit-route-${route.key}`}
                    className="p-2 rounded-full border border-white/10 text-zinc-300 hover:border-[#C5A059] hover:text-[#C5A059]">
              <Edit3 className="w-4 h-4" />
            </button>
            <button onClick={remove}
                    className="p-2 rounded-full border border-white/10 text-zinc-500 hover:border-red-500/60 hover:text-red-400">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function CategoryRow({ cat, isEditing, onEdit, onCancel, onSaved }) {
  const [multiplier, setMultiplier] = useState(cat.multiplier);
  const [capacity, setCapacity] = useState(cat.capacity);
  const [label, setLabel] = useState(cat.label);
  const [active, setActive] = useState(cat.active !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMultiplier(cat.multiplier); setCapacity(cat.capacity); setLabel(cat.label); setActive(cat.active !== false);
  }, [cat]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/pricing/vehicle-categories/${cat.id}`, {
        multiplier: Number(multiplier), capacity: Number(capacity), label, active,
      });
      toast.success("Category updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const preview = (180 * multiplier).toFixed(2);

  return (
    <tr data-testid={`cat-row-${cat.key}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
      <td className="px-5 py-4">
        {isEditing ? (
          <input value={label} onChange={(e) => setLabel(e.target.value)}
                 className="bg-zinc-800 border border-white/10 rounded px-2 py-1 w-full max-w-xs" />
        ) : (
          <>
            <div className="text-white">{cat.label}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{cat.key}</div>
          </>
        )}
      </td>
      <td className="px-3 py-4 text-right">
        {isEditing ? (
          <input type="number" value={capacity} onChange={(e) => setCapacity(e.target.value)}
                 className="w-16 bg-zinc-800 border border-white/10 rounded px-2 py-1 text-right" />
        ) : `${cat.capacity} pax`}
      </td>
      <td className="px-3 py-4 text-right">
        {isEditing ? (
          <input type="number" step="0.01" value={multiplier} data-testid={`cat-mult-${cat.key}`}
                 onChange={(e) => setMultiplier(e.target.value)}
                 className="w-20 bg-zinc-800 border border-white/10 rounded px-2 py-1 text-right" />
        ) : `×${cat.multiplier}`}
      </td>
      <td className="px-3 py-4 text-right text-[#C5A059]">€{preview}</td>
      <td className="px-3 py-4 text-right">
        {isEditing ? (
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>{active ? "Active" : "Off"}</span>
          </label>
        ) : (
          <span className={`text-xs uppercase tracking-wider ${cat.active !== false ? "text-emerald-400" : "text-zinc-600"}`}>
            {cat.active !== false ? "Active" : "Off"}
          </span>
        )}
      </td>
      <td className="px-5 py-4 text-right">
        {isEditing ? (
          <div className="inline-flex gap-2">
            <button onClick={save} disabled={saving} data-testid={`save-cat-${cat.key}`}
                    className="p-2 rounded-full bg-[#C5A059] text-black hover:bg-[#D4AF37] disabled:opacity-50">
              <Save className="w-4 h-4" />
            </button>
            <button onClick={onCancel} className="p-2 rounded-full border border-white/10 text-zinc-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button onClick={onEdit} data-testid={`edit-cat-${cat.key}`}
                  className="p-2 rounded-full border border-white/10 text-zinc-300 hover:border-[#C5A059] hover:text-[#C5A059]">
            <Edit3 className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

function SettingsPanel({ settings, onSaved }) {
  const [nightMult, setNightMult] = useState(settings.night_multiplier || 1.2);
  const [saving, setSaving] = useState(false);
  const [billing, setBilling] = useState({ vat_registered: false, vat_rate_pct: 25, company_iban: "", company_bic: "" });
  const [savingBilling, setSavingBilling] = useState(false);
  useEffect(() => setNightMult(settings.night_multiplier || 1.2), [settings]);
  useEffect(() => {
    api.get("/admin/billing-settings").then(({ data }) => setBilling(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/pricing/settings", { night_multiplier: Number(nightMult) });
      toast.success("Settings saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveBilling = async () => {
    setSavingBilling(true);
    try {
      const { data } = await api.patch("/admin/billing-settings", {
        vat_registered: billing.vat_registered,
        vat_rate_pct: Number(billing.vat_rate_pct),
        company_iban: billing.company_iban,
        company_bic: billing.company_bic,
      });
      setBilling(data);
      toast.success("PDV / billing settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingBilling(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-8 max-w-xl">
      <h3 className="font-display text-xl mb-1">Global settings</h3>
      <p className="text-sm text-zinc-500 mb-6">These apply to every route unless overridden per-route.</p>

      <div className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">
            Night multiplier · applied 22:00 – 05:59
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number" step="0.01" min="1" max="3"
              value={nightMult}
              data-testid="night-multiplier"
              onChange={(e) => setNightMult(e.target.value)}
              className="w-32 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-lg"
            />
            <span className="text-sm text-zinc-500">
              e.g. 1.20 = +20 %; use 1.00 to disable
            </span>
          </div>
          <p className="text-xs text-zinc-600 mt-2">
            €180 route at night → €{(180 * (nightMult || 1)).toFixed(2)}
          </p>
        </div>

        <button
          onClick={save} disabled={saving}
          data-testid="save-settings"
          className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#C5A059] text-black text-sm font-medium hover:bg-[#D4AF37] disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> Save settings
        </button>
      </div>

      <div className="mt-10 pt-8 border-t border-white/10">
        <h3 className="font-display text-xl mb-1">PDV i podaci za fakturiranje</h3>
        <p className="text-sm text-zinc-500 mb-6">
          Primjenjuje se globalno — na sve račune u sustavu (B2C i B2B R-1 računi).
        </p>
        <div className="space-y-5">
          <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/30 px-4 py-3">
            <div>
              <p className="text-sm text-white">U sustavu PDV-a</p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {billing.vat_registered
                  ? `Računi iskazuju osnovicu + PDV ${Number(billing.vat_rate_pct) || 0}%`
                  : "Mali obveznik — računi bez PDV-a uz napomenu čl. 90. st. 2. Zakona o PDV-u"}
              </p>
            </div>
            <button
              data-testid="vat-toggle"
              onClick={() => setBilling({ ...billing, vat_registered: !billing.vat_registered })}
              className={`relative w-12 h-6 rounded-full transition-colors ${billing.vat_registered ? "bg-[#C5A059]" : "bg-zinc-700"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${billing.vat_registered ? "left-6" : "left-0.5"}`} />
            </button>
          </div>

          {billing.vat_registered && (
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">Stopa PDV-a (%)</label>
              <input
                type="number" step="0.1" min="0" max="30"
                data-testid="vat-rate-input"
                value={billing.vat_rate_pct}
                onChange={(e) => setBilling({ ...billing, vat_rate_pct: e.target.value })}
                className="w-32 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-lg"
              />
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">Company IBAN</label>
              <input
                data-testid="company-iban-input"
                value={billing.company_iban}
                onChange={(e) => setBilling({ ...billing, company_iban: e.target.value })}
                placeholder="HR12 2340 0091 …"
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">SWIFT / BIC</label>
              <input
                data-testid="company-bic-input"
                value={billing.company_bic}
                onChange={(e) => setBilling({ ...billing, company_bic: e.target.value })}
                placeholder="PBZGHR2X"
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <button
            onClick={saveBilling} disabled={savingBilling}
            data-testid="save-billing-settings"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#C5A059] text-black text-sm font-medium hover:bg-[#D4AF37] disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> Save PDV / billing
          </button>
        </div>
      </div>
    </div>
  );
}

function NewRouteModal({ onClose, onSaved }) {
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [base, setBase] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!pickup.trim() || !dropoff.trim() || !base) {
      toast.error("Fill in pickup, drop-off and base price");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/pricing/routes", {
        pickup: pickup.trim(),
        dropoff: dropoff.trim(),
        base_price_eur: Number(base),
        endpoints: [], // free-form for now; endpoint mapping stays in service defaults
      });
      toast.success("Route added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-8 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl">New route</h3>
          <button onClick={onClose} data-testid="close-new-route" className="p-2 rounded-full hover:bg-white/5">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Pickup</label>
            <input value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder="Split"
                   data-testid="new-route-pickup"
                   className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Drop-off</label>
            <input value={dropoff} onChange={(e) => setDropoff(e.target.value)} placeholder="Zagreb"
                   data-testid="new-route-dropoff"
                   className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Base price €</label>
            <input type="number" step="0.01" value={base} onChange={(e) => setBase(e.target.value)}
                   data-testid="new-route-price"
                   className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2" />
          </div>
        </div>
        <button onClick={save} disabled={saving}
                data-testid="new-route-save"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C5A059] text-black font-medium hover:bg-[#D4AF37] disabled:opacity-50">
          <Save className="w-4 h-4" /> Add route
        </button>
      </div>
    </div>
  );
}
